import { useSyncExternalStore } from "react";

export type ResolvedTheme = "light" | "dark";
type ThemePreference = ResolvedTheme | "system";

/** Keep in sync with public/theme-init.js, which applies the saved theme before first paint. */
export const THEME_STORAGE_KEY = "ravnar-theme";
const darkSchemeQuery = "(prefers-color-scheme: dark)";

export function parseThemePreference(value: string | null): ThemePreference {
	return value === "light" || value === "dark" ? value : "system";
}

function readSavedPreference(): ThemePreference {
	try {
		return parseThemePreference(localStorage.getItem(THEME_STORAGE_KEY));
	} catch {
		// Storage can be unavailable (private browsing, blocked site data): follow the OS preference.
		return "system";
	}
}

// Module state lives in an object so functions update a property instead of reassigning a module-level binding.
const store = { preference: readSavedPreference(), listeners: new Set<() => void>() };

function getResolvedTheme(): ResolvedTheme {
	if (store.preference === "system") {
		return window.matchMedia(darkSchemeQuery).matches ? "dark" : "light";
	}
	return store.preference;
}

function applyResolvedTheme(): void {
	document.documentElement.classList.toggle("dark", getResolvedTheme() === "dark");
}

function subscribe(listener: () => void): () => void {
	const media = window.matchMedia(darkSchemeQuery);
	const onSystemChange = () => {
		applyResolvedTheme();
		listener();
	};
	const onStorage = (event: StorageEvent) => {
		if (event.key !== THEME_STORAGE_KEY) {
			return;
		}
		store.preference = parseThemePreference(event.newValue);
		applyResolvedTheme();
		listener();
	};
	store.listeners.add(listener);
	media.addEventListener("change", onSystemChange);
	window.addEventListener("storage", onStorage);
	return () => {
		store.listeners.delete(listener);
		media.removeEventListener("change", onSystemChange);
		window.removeEventListener("storage", onStorage);
	};
}

/** Saves an explicit choice, applies it to <html> and re-renders every `useResolvedTheme()` consumer. */
export function setTheme(theme: ResolvedTheme): void {
	store.preference = theme;
	try {
		localStorage.setItem(THEME_STORAGE_KEY, theme);
	} catch {
		// Not persisted when storage is unavailable; the choice still applies until the page reloads.
	}
	applyResolvedTheme();
	for (const listener of store.listeners) {
		listener();
	}
}

/** The theme in effect: the saved choice, otherwise the OS color-scheme preference. */
export function useResolvedTheme(): ResolvedTheme {
	return useSyncExternalStore(subscribe, getResolvedTheme);
}
